import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  RiMenuLine,
  RiCloseLine,
  RiArrowDownSLine,
  RiArrowUpSLine,
} from 'react-icons/ri';
import { Link } from 'react-router-dom';
import './Header.css';
import HeaderLogo from '../../assets/images/Header/logo.png';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // для десктопа
  const [activeCategory, setActiveCategory] = useState(null); // для десктоп-сложных меню
  const [isMobile, setIsMobile] = useState(false);

  // мобильные аккордеоны
  const [openMobileSection, setOpenMobileSection] = useState(null); // aboutcompany / services / jurisdictions
  const [openMobileSubcategory, setOpenMobileSubcategory] = useState(null); // id категории внутри services/jurisdictions

  const headerRef = useRef(null);

  // Определяем мобильное устройство
  useEffect(() => {
    const checkMobile = () => {
      const isNowMobile = window.innerWidth < 1118;
      setIsMobile(isNowMobile);

      // Если ушли с мобилки на десктоп — закрываем мобильное меню/аккордеоны
      if (!isNowMobile) {
        setMenuOpen(false);
        setOpenMobileSection(null);
        setOpenMobileSubcategory(null);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => {
      const next = !prev;
      if (!next) {
        // при закрытии меню чистим моб. состояния
        setOpenMobileSection(null);
        setOpenMobileSubcategory(null);
      }
      return next;
    });
  }, []);

  const toggleDropdown = useCallback(
    (key) => {
      if (openDropdown === key) {
        setOpenDropdown(null);
        setActiveCategory(null);
      } else {
        setOpenDropdown(key);
        if (key === 'services' || key === 'jurisdictions') {
          const firstCategory =
            key === 'services' ? 'gaming-license' : 'jurisdictions';
          setActiveCategory(firstCategory);
        }
      }
    },
    [openDropdown]
  );

  const closeAll = useCallback(() => {
    setMenuOpen(false);
    setOpenDropdown(null);
    setActiveCategory(null);
    setOpenMobileSection(null);
    setOpenMobileSubcategory(null);
  }, []);

  const scrollToTopAndCloseMenu = () => {
    window.scrollTo(0, 0);
    closeAll();
  };

  const handleClickOutside = useCallback(
    (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        closeAll();
      }
    },
    [closeAll]
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [handleClickOutside]);

  // Блокируем скролл body при открытом моб. меню
  useEffect(() => {
    if (menuOpen && isMobile) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }

    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [menuOpen, isMobile]);

  const renderDropdownArrow = (isOpen) =>
    isOpen ? (
      <RiArrowUpSLine className="dropdown-arrow" />
    ) : (
      <RiArrowDownSLine className="dropdown-arrow" />
    );

  // Данные для категорий Services
  const servicesCategories = [
    {
      id: 'gaming-license',
      name: 'Gaming License',
      links: [
        { name: 'Gaming License Services', link: '/gaming-license-services' },
        {
          name: 'iGaming & Gambling License – Western Sahara',
          link: '/igaming-gambling-western-sahara',
        },
        {
          name: 'Gaming License – Mwali (Comoros)',
          link: '/gaming-license-mwali',
        },
        {
          name: 'Gaming License – Anjouan (Comoros)',
          link: '/gaming-license-anjouan',
        },
        {
          name: 'Gibraltar iGaming License',
          link: '/gibraltar-igaming-license',
        },
        {
          name: 'Gaming & iGaming License – Malta (MGA)',
          link: '/gaming-igaming-license-malta',
        },
        {
          name: 'Gaming & Betting License – Cyprus',
          link: '/gaming-betting-license–cyprus',
        },
        {
          name: 'Gaming License – Panama',
          link: '/gaming-license–panama',
        },
        {
          name: 'Curaçao Gaming License',
          link: '/curaçao-gaming-license',
        },
        {
          name: 'Gaming & iGaming License – Seychelles',
          link: '/gaming-igaming-license–seychelles',
        },
        {
          name: 'Gaming & Betting License – Saint Lucia',
          link: '/gaming-betting-license–saint-lucia',
        },
      ],
    },
    {
      id: 'forex-brokerage',
      name: 'Forex & Brokerage License',
      links: [
        {
          name: 'Brokerage & Forex Licenses',
          link: '/brokerage-forex-licenses',
        },
        {
          name: 'Forex & Brokerage License in Western Sahara',
          link: '/forex-brokerage-license-western-sahara',
        },
        {
          name: 'Forex & Brokerage License – Mwali (Comoros)',
          link: '/forex-brokerage-license-mwali',
        },
        {
          name: 'Brokerage/FX & Clearing House License – Anjouan (Comoros)',
          link: '/brokerage-license-anjouan',
        },
        {
          name: 'Brokerage & Forex License – Malta (MFSA)',
          link: '/brokerage-forex-license-malta',
        },
        {
          name: 'Forex & Brokerage License – Cyprus (CySEC / CIF)',
          link: '/forex-brokerage-license-cyprus',
        },
        {
          name: 'Securities Dealer License – Seychelles',
          link: '/securities-dealer-license-seychelles',
        },
        {
          name: 'Forex & Brokerage – St. Vincent & the Grenadines',
          link: '/forex-brokerage-St-vincent-grenadines',
        },
      ],
    },
    {
      id: 'brokerage-forex-lic',
      name: 'Brokerage & Forex License',
      links: [
        {
          name: 'Brokerage License',
          link: '/brokerage-forex-license-malta',
        },
        {
          name: 'Brokerage – W. Sahara',
          link: '/brokerage-forex-license-malta',
        },
        {
          name: 'Brokerage – Mwali',
          link: '/brokerage-forex-license-malta',
        },
        {
          name: 'Brokerage – anjouan-offshore-offshore-company-formation',
          link: '/brokerage-forex-license-malta',
        },
        {
          name: 'Brokerage – Cyprus',
          link: '/brokerage-forex-license-malta',
        },
        {
          name: 'Brokerage – Seychelles',
          link: '/brokerage-forex-license-malta',
        },
        {
          name: 'Brokerage – Malta',
          link: '/brokerage-forex-license-malta',
        },
        {
          name: 'Brokerage – St. Lucia',
          link: '/brokerage-forex-license-malta',
        },
      ],
    },
    {
      id: 'banking',
      name: 'Banking License',
      links: [
        { name: 'Banking License', link: '/banking-license' },
        {
          name: 'Banking License – Malta (MFSA/ECB)',
          link: '/banking-license-malta',
        },
        {
          name: 'Banking License – Cyprus',
          link: '/banking-license-cyprus',
        },
        {
          name: 'Banking License – Panama',
          link: '/banking-license-panama',
        },
        {
          name: 'Banking License – Seychelles',
          link: '/banking-license-seychelles',
        },
        {
          name: 'Banking License – Mauritius',
          link: '/banking-license-mauritius',
        },
        {
          name: 'Banking License – Saint Lucia',
          link: '/banking-license-saint-lucia',
        },
        {
          name: 'Western Sahara Banking License',
          link: '/western-sahara-banking-license',
        },
        {
          name: 'Banking License – Mwali (Comoros)',
          link: '/banking-license-mwali',
        },
      ],
    },
    {
      id: 'crypto',
      name: 'Crypto & Blockchain',
      links: [
        { name: 'Crypto & Virtual Asset Licenses', link: '/crypto-virtual-asset-licenses' },
        {
          name: 'Crypto License – Western Sahara',
          link: '/crypto-license-western-sahara',
        },
        {
          name: 'Crypto License – Mwali',
          link: '/crypto-license-mwali',
        },
        {
          name: 'Crypto License – Anjouan',
          link: '/crypto-license-anjouan',
        },
        {
          name: 'Gibraltar Crypto & DLT License',
          link: '/gibraltar-crypto-dlt-license',
        },
        {
          name: 'Crypto & Blockchain License – Malta (VFA Act)',
          link: '/crypto-blockchain-license-malta',
        },
        {
          name: 'Crypto & Blockchain License – Cyprus (CySEC CASP)',
          link: '/crypto-blockchain-license-cyprus',
        },
        {
          name: 'Crypto License – Panama',
          link: '/crypto-license-panama',
        },
        {
          name: 'Crypto & Digital Asset License – Seychelles',
          link: '/crypto-digital-asset-license-seychelles',
        },
        {
          name: 'Crypto & VASP License – Mauritius',
          link: '/Crypto-vasp-license-mauritius',
        },
      ],
    },
    {
      id: 'trust',
      name: 'Trust & Insurance Licenses',
      links: [
        { name: 'Trust & Insurance Licenses', link: '/trust-insurance-licenses' },
        {
          name: 'Trust & Insurance Licenses - Western Sahara',
          link: '/trust-insurance-licenses-western-sahara',
        },
        {
          name: 'Trust & Insurance Licenses Mwali (Comoros)',
          link: '/trust-insurance-licenses-mwali',
        },
        {
          name: 'Trust & Insurance Licenses Anjouan (Comoros)',
          link: '/trust-insurance-licenses-anjouan',
        },
        {
          name: 'Gibraltar Insurance & Reinsurance License',
          link: '/gibraltar-insurance-reinsurance-license',
        },
        {
          name: 'Insurance & Reinsurance License – Malta (MFSA)',
          link: '/insurance-reinsurance-license-malta',
        },
        {
          name: 'Insurance & Reinsurance License – Cyprus',
          link: '/insurance-reinsurance-cyprus',
        },
        {
          name: 'Insurance & Reinsurance License – Panama',
          link: '/insurance-reinsurance-license-panama',
        },
        {
          name: 'Insurance & Captives – Isle of Man',
          link: '/insurance-captives-isle-man',
        },
        {
          name: 'Insurance & Reinsurance License – Mauritius',
          link: '/insurance-reinsurance-license-mauritius',
        },
      ],
    },
    {
      id: 'fund',
      name: 'Fund & Asset Management',
      links: [
        { name: 'Funds & Asset Management – Isle of Man', link: '/funds-asset-management-isle-man' },
        {
          name: 'Funds & Asset Management License – Malta (MFSA)',
          link: '/funds-asset-management-license-malta',
        },
        {
          name: 'Funds & Asset Management License – Cyprus (CySEC)',
          link: '/funds-asset-management-license-cyprus',
        },
        {
          name: 'Funds & Asset Management License – Panama',
          link: '/funds-asset-management-license-panama',
        },
        {
          name: 'Asset Management License – Mwali (Comoros)',
          link: '/asset-management-license-mwali',
        },
        {
          name: 'Funds & Asset Management License – Mauritius',
          link: '/funds-asset-management-license-mauritius',
        },
        {
          name: 'Asset Management License – Western Sahara',
          link: '/funds-asset-management-license-mauritius',
        },
      ],
    },
    {
      id: 'payment',
      name: 'Payment & FinTech',
      links: [
        { name: 'Payments & FinTech Licenses', link: '/payments-fintech-licenses' },
        {
          name: 'Gibraltar Banking & FinTech License',
          link: '/gibraltar-banking-finTech-license',
        },
        {
          name: 'Payment Services & EMI License – Malta (MFSA)',
          link: '/payment-services-EMI-license-malta',
        },
        {
          name: 'Payment Services & EMI License – Cyprus (Central Bank of Cyprus)',
          link: '/payment-services-EMI-license-cyrus',
        },
        {
          name: 'Payments & FinTech License – Mauritius',
          link: '/payments-fintech-license-mauritius',
        },
        {
          name: 'Payments & FinTech License – Seychelles',
          link: '/payments-fintech-license-seychelles',
        },
        {
          name: 'FinTech & Payments License – Panama',
          link: '/payments-fintech-license-panama',
        },
      ],
    },
    {
      id: 'areas',
      name: 'Areas',
      links: [
        { name: 'Offshore Company Registration', link: '/assets-protection' },
        { name: 'Licensing', link: '/financial-license' },
        {
          name: 'Opening bank accounts',
          link: '/offshore-bank-account-opening',
        },
        {
          name: 'Residency and Passport',
          link: '/residency-passport',
        },
        {
          name: 'Asset Management and Due Diligence',
          link: '/assets-protection',
        },
        { name: 'Ready-Made Companies', link: '/ready-made' },
        { name: 'Fintech Services', link: '/fintech-services' },
        {
          name: 'Meta Financial License',
          link: '/financial-license',
        },
      ],
    },
  ];

  // Данные для категорий Jurisdictions
  const jurisdictionsCategories = [
    {
      id: 'jurisdictions',
      name: 'jurisdiction',
      links: [
        {
          name: 'Western Sahara Jurisdiction',
          link: '/western-sahara-jurisdiction',
        },
        {
          name: 'Mwali Jurisdiction',
          link: '/mwali-jurisdiction',
        },
        {
          name: 'Anjouan Jurisdiction',
          link: '/anjouan-jurisdiction',
        },
        {
          name: 'Isle of Man as a Jurisdiction',
          link: '/isle-man-jurisdiction',
        },
        {
          name: 'Malta Jurisdiction',
          link: '/malta-jurisdiction',
        },
        {
          name: 'Cyprus Jurisdiction',
          link: '/cyprus-jurisdiction',
        },
        {
          name: 'Panama Jurisdiction',
          link: '/panama-jurisdiction',
        },
        {
          name: 'Seychelles Jurisdiction',
          link: '/seychelles-jurisdiction',
        },
        {
          name: 'Curaçao Jurisdiction',
          link: '/mauritius-jurisdiction',
        },
         {
          name: 'St. Vincent & the Grenadines (SVG) as a Jurisdiction',
          link: '/st-vincent-jurisdiction',
        },
        {
          name: 'United Arab Emirates (UAE) Jurisdiction',
          link: '/united-arab-emirates-jurisdiction',
        },
        {
          name: 'United Kingdom Jurisdiction',
          link: '/united-kingdom-jurisdiction',
        },
        {
          name: 'Singapore Jurisdiction',
          link: '/singapore-jurisdiction',
        },
        {
          name: 'Indonesia Jurisdiction',
          link: '/indonesia-jurisdiction',
        },
        {
          name: 'Greece Jurisdiction',
          link: '/greece-jurisdiction',
        },
        {
          name: 'Spain Jurisdiction',
          link: '/spain-jurisdiction',
        },
        {
          name: 'Italy Jurisdiction',
          link: '/italy-jurisdiction',
        },
        {
          name: 'Dominica Jurisdiction',
          link: '/dominica-jurisdiction',
        },
        {
          name: 'Maritime & Shipping Licenses – Panama',
          link: '/maritime-shipping-license-panama',
        },
      ],
    },
    {
      id: 'company-formation',
      name: 'Company Formation',
      links: [
        {
          name: 'Offshore company-formation',
          link: '/offshore-company-formation',
        },
        {
          name: 'Western Sahara IBC (International Business Company)',
          link: '/western-sahara-ibc',
        },
        {
          name: 'Mwali IBC (International Business Company)',
          link: '/mwali-ibc',
        },
        {
          name: 'Anjouan IBC (International Business Company)',
          link: '/anjouan-ibc',
        },
        {
          name: 'Cyprus company-formation',
          link: '/cyprus-company-formation',
        },
        {
          name: 'St. Lucia company-formation',
          link: '/st-lucia-company-formation',
        },
        {
          name: 'UK company-formation',
          link: '/uk-company-formation',
        },
        {
          name: 'Indonesia company-formation (PT PMA)',
          link: '/indonesia-company-formation',
        },
        {
          name: 'Panama company-formation',
          link: '/panama-company-formation',
        },
        {
          name: 'Malta company-formation',
          link: '/malta-company-formation',
        },
        {
          name: 'Mauritius company-formation',
          link: '/mauritius-company-formation',
        },
        {
          name: 'Isle of Man company-formation',
          link: '/isle-man-company-formation',
        },
        {
          name: 'St. Vincent company-formation',
          link: '/st-vincent-company-formation',
        },
        {
          name: 'UAE company-formation',
          link: '/uae--company-formation',
        },
        {
          name: 'Singapore company-formation',
          link: '/singapore-company-formation',
        },
        {
          name: 'company-formation in Seychelles',
          link: '/seychelles-company-formation',
        },
        {
          name: 'company-formation in Dominica',
          link: '/dominica-company-formation',
        },
        {
          name: 'Residency & Investment – Gibraltar',
          link: '/company-formation-gibraltar',
        },
        {
          name: 'Anjouan Offshore company-formation',
          link: '/anjouan-jurisdiction',
        },
        {
          name: 'Western Sahara Offshore company-formation',
          link: '/western-sahara-jurisdiction',
        },
        {
          name: 'Cyprus Offshore company-formation',
          link: '/cyprus-jurisdiction',
        },
      ],
    },
    {
      id: 'residency',
      name: 'Residency & Passport',
      links: [
        {
          name: 'Residency and Passport',
          link: '/residency-passport',
        },
        {
          name: 'Residency & Investment UAE',
          link: '/residency-passport',
        },
        { name: 'Golden visa Spain', link: '/golden-visa-spain' },
        { name: 'Golden visa Italy', link: '/golden-visa-italy' },
        { name: 'Residency Indonesia', link: '/residency-indonesia' },
        {
          name: 'Residency & Investment Cyprus',
          link: '/residency-investment-cyprus',
        },
        {
          name: 'Residency Citizenship Dominica',
          link: '/residency-citizenship-dominica',
        },
        {
          name: 'Residency & Investment Gibraltar',
          link: '/residency-investment-gibraltar',
        },
        {
          name: 'Residency & Investment – Isle of Man',
          link: '/residency-investment-isle-man',
        },
      ],
    },
    {
      id: 'investment',
      name: 'Real Estate Investment',
      links: [
        {
          name: 'Real Estate Investment',
          link: '/real-estate-investment',
        },
        {
          name: 'Real Estate Investment In UAE',
          link: '/real-estate-investment-uae',
        },
        { name: 'Real Estate Investment in Bali, Indonesia', link: '/real-estate-investment-bali-indonesia' },
        { name: 'Real Estate Investment in Cyprus', link: '/real-estate-investment-cyprus' },
        { name: 'Real Estate Investment in Italy', link: '/real-estate-investment-italy' },
        {
          name: 'Real Estate Investment in Greece',
          link: '/real-estate-investment-greece',
        },
      ],
    },
    {
      id: 'tax',
      name: 'Non-Dom Tax Regime',
      links: [
        {
          name: 'UK Non-Domiciled (Non-Dom) Tax Regime',
          link: '/uk-non-domiciled-tax-regime',
        },
        {
          name: 'Non Dom Tax Regime',
          link: '/non-dom-tax-regime',
        },
        { name: 'Italy Non Dom Tax Regime', link: '/ital-non-dom-tax-regime' },
      ],
    },
    {
      id: 'advisory',
      name: 'Financial & Advisory',
      links: [
        {
          name: 'Offshore bank account set up',
          link: '/offshore-bank-account-opening',
        },
        {
          name: 'FinTech Services',
          link: '/fintech-services',
        },
        { name: 'Financial License', link: '/financial-license' },
        { name: 'Ready Made Companies', link: '/ready-made' },
        { name: 'Ready-Made Companies with Corporate Bank Accounts', link: '/ready-made-companies-corporate-bank-accounts' },
        { name: 'Assets Protection', link: '/assets-protection' },
      ],
    },
  ];

  // Рендеринг простого дропдауна (десктоп)
  const renderSimpleDropdown = (links, title = null) => (
    <div className="dropdown-content simple-dropdown">
      <div className="dropdown-group">
        {title && <span className="dropdown-title">{title}</span>}
        <ul className="dropdown-list">
          {links.map((item, index) => (
            <li key={index}>
              <Link
                to={item.link}
                className="dropdown-link"
                onClick={scrollToTopAndCloseMenu}
              >
                {item.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  // Рендеринг сложного дропдауна (десктоп)
  const renderComplexDropdown = (type) => {
    const categories =
      type === 'services' ? servicesCategories : jurisdictionsCategories;
    const currentCategory =
      categories.find((cat) => cat.id === activeCategory) || categories[0];

    return (
      <div className="dropdown-content complex-dropdown">
        <div className="dropdown-navigation">
          <div className="nav-categories">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`nav-category ${
                  activeCategory === category.id ? 'active' : ''
                }`}
                onClick={() => setActiveCategory(category.id)}
                type="button"
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
        <div className="dropdown-content-container">
          <div className="dropdown-group">
            <span className="dropdown-title">{currentCategory.name}</span>
            <ul className="dropdown-list">
              {currentCategory.links.map((item, index) => (
                <li key={index}>
                  <Link
                    to={item.link}
                    className="dropdown-link"
                    onClick={scrollToTopAndCloseMenu}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Навигация для десктопа
  const renderDesktopNav = () => (
    <ul className="nav-list">
      {/* About Company */}
      <li
        className={`dropdown-item ${
          openDropdown === 'aboutcompany' ? 'show-dropdown' : ''
        }`}
      >
        <div
          className="nav-link dropdown-button"
          onClick={() => toggleDropdown('aboutcompany')}
          role="button"
          tabIndex={0}
        >
          About Company {renderDropdownArrow(openDropdown === 'aboutcompany')}
        </div>
        {openDropdown === 'aboutcompany' && (
          <div className="dropdown-container">
            {renderSimpleDropdown([
              { name: 'About the Company', link: '/about' },
              { name: 'FAQ', link: '/faq' },
              {
                name: 'Why Offshore Advisory',
                link: '/why-offshore-advisory',
              },
              {
                name: 'Client Testimonials',
                link: '/client-testimonials',
              },
            ])}
          </div>
        )}
      </li>

      {/* Services */}
      <li
        className={`dropdown-item ${
          openDropdown === 'services' ? 'show-dropdown' : ''
        }`}
      >
        <div
          className="nav-link dropdown-button"
          onClick={() => toggleDropdown('services')}
          role="button"
          tabIndex={0}
        >
          Services {renderDropdownArrow(openDropdown === 'services')}
        </div>
        {openDropdown === 'services' && (
          <div className="dropdown-container wide-dropdown">
            {renderComplexDropdown('services')}
          </div>
        )}
      </li>

      {/* Jurisdictions */}
      <li
        className={`dropdown-item ${
          openDropdown === 'jurisdictions' ? 'show-dropdown' : ''
        }`}
      >
        <div
          className="nav-link dropdown-button"
          onClick={() => toggleDropdown('jurisdictions')}
          role="button"
          tabIndex={0}
        >
          Offshore Structures {renderDropdownArrow(openDropdown === 'jurisdictions')}
        </div>
        {openDropdown === 'jurisdictions' && (
          <div className="dropdown-container wide-dropdown">
            {renderComplexDropdown('jurisdictions')}
          </div>
        )}
      </li>

      {/* Простые ссылки */}
      <li>
        <Link
          to="/contact"
          className="nav-link"
          onClick={scrollToTopAndCloseMenu}
        >
          Contact us
        </Link>
      </li>

      <li>
        <Link
          to="/blogs"
          className="nav-link"
          onClick={scrollToTopAndCloseMenu}
        >
          Blogs
        </Link>
      </li>

      <li>
        <Link
          to="/newsletter"
          className="subscribe-link"
          onClick={scrollToTopAndCloseMenu}
        >
          Subscribe
        </Link>
      </li>
    </ul>
  );

  // Навигация для мобильных: те же пункты, но аккордеоны
  const renderMobileNav = () => {
    const toggleSection = (sectionKey) => {
      setOpenMobileSection((prev) =>
        prev === sectionKey ? null : sectionKey
      );
      setOpenMobileSubcategory(null);
    };

    const toggleSubcategory = (subcategoryId) => {
      setOpenMobileSubcategory((prev) =>
        prev === subcategoryId ? null : subcategoryId
      );
    };

    const aboutLinks = [
      { name: 'About the Company', link: '/about' },
      { name: 'FAQ', link: '/faq' },
      {
        name: 'Why Offshore Advisory',
        link: '/why-offshore-advisory',
      },
      {
        name: 'Client Testimonials',
        link: '/client-testimonials',
      },
    ];

    return (
      <ul className="nav-list mobile-nav">
        {/* About Company */}
        <li
          className={`mobile-dropdown ${
            openMobileSection === 'aboutcompany' ? 'open' : ''
          }`}
        >
          <button
            type="button"
            className="mobile-dropdown-toggle"
            onClick={() => toggleSection('aboutcompany')}
          >
            <span>About Company</span>
            {renderDropdownArrow(openMobileSection === 'aboutcompany')}
          </button>
          {openMobileSection === 'aboutcompany' && (
            <div className="mobile-dropdown-content">
              {aboutLinks.map((item) => (
                <Link
                  key={item.link}
                  to={item.link}
                  className="mobile-nav-link mobile-nav-link-inner"
                  onClick={scrollToTopAndCloseMenu}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          )}
        </li>

        {/* Services (сложное меню: категории + ссылки) */}
        <li
          className={`mobile-dropdown ${
            openMobileSection === 'services' ? 'open' : ''
          }`}
        >
          <button
            type="button"
            className="mobile-dropdown-toggle"
            onClick={() => toggleSection('services')}
          >
            <span>Services</span>
            {renderDropdownArrow(openMobileSection === 'services')}
          </button>
          {openMobileSection === 'services' && (
            <div className="mobile-dropdown-content">
              {servicesCategories.map((category) => (
                <div
                  key={category.id}
                  className={`mobile-subcategory ${
                    openMobileSubcategory === category.id ? 'open' : ''
                  }`}
                >
                  <button
                    type="button"
                    className="mobile-subcategory-toggle"
                    onClick={() => toggleSubcategory(category.id)}
                  >
                    <span>{category.name}</span>
                    {renderDropdownArrow(
                      openMobileSubcategory === category.id
                    )}
                  </button>
                  {openMobileSubcategory === category.id && (
                    <ul className="mobile-subcategory-list">
                      {category.links.map((item) => (
                        <li key={item.link}>
                          <Link
                            to={item.link}
                            className="mobile-nav-link mobile-nav-link-deep"
                            onClick={scrollToTopAndCloseMenu}
                          >
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </li>

        {/* Jurisdictions (сложное меню: категории + ссылки) */}
        <li
          className={`mobile-dropdown ${
            openMobileSection === 'jurisdictions' ? 'open' : ''
          }`}
        >
          <button
            type="button"
            className="mobile-dropdown-toggle"
            onClick={() => toggleSection('jurisdictions')}
          >
            <span>Offshore Structures</span>
            {renderDropdownArrow(openMobileSection === 'jurisdictions')}
          </button>
          {openMobileSection === 'jurisdictions' && (
            <div className="mobile-dropdown-content">
              {jurisdictionsCategories.map((category) => (
                <div
                  key={category.id}
                  className={`mobile-subcategory ${
                    openMobileSubcategory === category.id ? 'open' : ''
                  }`}
                >
                  <button
                    type="button"
                    className="mobile-subcategory-toggle"
                    onClick={() => toggleSubcategory(category.id)}
                  >
                    <span>{category.name}</span>
                    {renderDropdownArrow(
                      openMobileSubcategory === category.id
                    )}
                  </button>
                  {openMobileSubcategory === category.id && (
                    <ul className="mobile-subcategory-list">
                      {category.links.map((item) => (
                        <li key={item.link}>
                          <Link
                            to={item.link}
                            className="mobile-nav-link mobile-nav-link-deep"
                            onClick={scrollToTopAndCloseMenu}
                          >
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </li>

        {/* Простые ссылки без подменю */}
        <li className="mobile-simple-item">
          <Link
            to="/contact"
            className="mobile-nav-link"
            onClick={scrollToTopAndCloseMenu}
          >
            Contact us
          </Link>
        </li>

        <li className="mobile-simple-item">
          <Link
            to="/blogs"
            className="mobile-nav-link"
            onClick={scrollToTopAndCloseMenu}
          >
            Blogs
          </Link>
        </li>

        <li className="mobile-simple-item">
          <Link
            to="/newsletter"
            className="subscribe-link mobile-subscribe"
            onClick={scrollToTopAndCloseMenu}
          >
            Subscribe
          </Link>
        </li>
      </ul>
    );
  };

  return (
    <header className="header" ref={headerRef}>
      <nav className="nav container">
        <div className="nav-data">
          <Link to="/" className="nav-logo" onClick={closeAll}>
            <img
              src={HeaderLogo}
              alt="header logo"
              className="header-logo-img"
            />
            Offshore Advisory
          </Link>

          {/* Бургер-меню показываем только на мобильных */}
          {isMobile && (
            <button
              type="button"
              className="nav-toggle"
              onClick={toggleMenu}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
            >
              {menuOpen ? (
                <RiCloseLine className="nav-toggle-close" />
              ) : (
                <RiMenuLine className="nav-toggle-menu" />
              )}
            </button>
          )}
        </div>

        {/* Навигация для десктопа */}
        {!isMobile && (
          <div className="nav-menu desktop-nav">{renderDesktopNav()}</div>
        )}

        {/* Мобильное меню */}
        {isMobile && (
          <div className={`mobile-menu ${menuOpen ? 'show-menu' : ''}`}>
            {renderMobileNav()}
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
